import React from "react";
import { useMsal } from "@azure/msal-react";
import { QRCode } from "react-qrcode-logo";
import {
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  Paper,
  Divider,
  Box,
  Alert,
} from "@mui/material";
import Download from "@mui/icons-material/Download";
import { colorOptions } from "../../context/colorOptions";
import { logActivity } from "../../functions/axios";

export default function VCardGenerator() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const qrCodeRef = React.createRef();

  const maxBits = 18672;
  const [error, setError] = React.useState("");
  const [vCardString, setVCardString] = React.useState("");

  const [vCard, setVCard] = React.useState({
    firstName: activeAccount?.name?.split(" ")[0] || "",
    lastName: activeAccount?.name?.split(" ")[1] || "",
    title: "",
    company: "Haskoning",
    jobTitle: "",
    phoneWork: "",
    phonePrivate: "",
    phoneMobile: "",
    email: activeAccount?.username || "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    website: "https://www.haskoning.com",
    website2: "",
    notes: "",
  });

  const [size, setSize] = React.useState(256);
  const sizeOptions = [
    // [128, "S"], too small for the QR code to be readable
    [256, "M"],
    [512, "L"],
  ];

  const [color, setColor] = React.useState(colorOptions[0]);

  const handleInputChange = (field, value) => {
    const newVCard = { ...vCard, [field]: value };
    const newVCardString = generateVCardString(newVCard);
    setVCardString(newVCardString);
    setVCard(newVCard);
  };
  React.useEffect(() => {
    const newVCardString = generateVCardString(vCard);
    setVCardString(newVCardString);
  }, []);

  const generateVCardString = (vCard) => {
    let vCardString = "BEGIN:VCARD\nVERSION:3.0\nKIND:individual\n";

    // Name
    if (vCard.firstName || vCard.lastName) {
      vCardString += `FN:${vCard.firstName} ${vCard.lastName}\n`;
      vCardString += `N:${vCard.lastName};${vCard.firstName};;;\n`;
    }

    // Organization
    if (vCard.company) {
      vCardString += `ORG:${vCard.company}\n`;
    }

    // Job Title
    if (vCard.jobTitle) {
      vCardString += `ROLE:${vCard.jobTitle}\n`;
    }

    // Phone numbers
    if (vCard.phoneWork) {
      vCardString += `TEL;VALUE=uri;TYPE=work:${vCard.phoneWork}\n`;
    }

    if (vCard.phonePrivate) {
      vCardString += `TEL;VALUE=uri;TYPE=home:${vCard.phonePrivate}\n`;
    }

    if (vCard.phoneMobile) {
      vCardString += `TEL;VALUE=uri;TYPE=home:${vCard.phoneMobile}\n`;
    }

    // Email
    if (vCard.email) {
      vCardString += `EMAIL:${vCard.email}\n`;
    }

    // Address
    if (vCard.address || vCard.city || vCard.state || vCard.zip || vCard.country) {
      vCardString += `ADR:;;${vCard.address};${vCard.city};${vCard.state};${vCard.zip};${vCard.country}\n`;
    }

    // Website
    if (vCard.website) {
      vCardString += `URL:${vCard.website}\n`;
    }

    if (vCard.website2) {
      vCardString += `URL:${vCard.website2}\n`;
    }

    // Notes
    if (vCard.notes) {
      vCardString += `NOTE:${vCard.notes}\n`;
    }

    vCardString += "END:VCARD";

    if (getStringSizeInBits(vCardString) > maxBits) {
      setError(
        "The vCard is too large to be encoded in a QR code. Please reduce the size of the vCard."
      );
      return "";
    }
    setError("");
    return vCardString;
  };

  const getStringSizeInBits = (string) => {
    let size = new Blob([string]).size * 8;

    return size;
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById("vcard-qr-code");
    if (canvas) {
      const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
      let downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = `${vCard.firstName}_${vCard.lastName}_vCard.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Log the download activity
      logActivity("vcard_download", activeAccount?.username);
    } else {
      console.error("No canvas found");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        vCard QR Code Generator
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Fill in your contact information to generate a vCard QR code that can be scanned by others
        to add you to their contacts. With very large vCards, the QR code may not be readable if the
        size is too small.
        <br />
        Either first or last name is required.
      </Typography>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} md={6}>
          <Paper>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>

            <Stack spacing={1}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="First Name"
                    value={vCard.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="Last Name"
                    value={vCard.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>

              <TextField
                size="small"
                label="Company"
                value={vCard.company}
                onChange={(e) => handleInputChange("company", e.target.value)}
                fullWidth
              />

              <TextField
                size="small"
                label="Job Title"
                value={vCard.jobTitle}
                onChange={(e) => handleInputChange("jobTitle", e.target.value)}
                fullWidth
              />

              <TextField
                size="small"
                label="Email"
                type="email"
                value={vCard.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                fullWidth
              />

              <Divider />
              <Typography variant="subtitle1" fontWeight="bold">
                Phone Numbers
              </Typography>

              <TextField
                size="small"
                label="Work Phone"
                value={vCard.phoneWork}
                onChange={(e) => handleInputChange("phoneWork", e.target.value)}
                fullWidth
              />

              <TextField
                size="small"
                label="Private Phone"
                value={vCard.phonePrivate}
                onChange={(e) => handleInputChange("phonePrivate", e.target.value)}
                fullWidth
              />

              <TextField
                size="small"
                label="Mobile Phone"
                value={vCard.phoneMobile}
                onChange={(e) => handleInputChange("phoneMobile", e.target.value)}
                fullWidth
              />

              <Divider />
              <Typography variant="subtitle1" fontWeight="bold">
                Address
              </Typography>
              <TextField
                size="small"
                label="Street Address"
                value={vCard.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                fullWidth
              />
            </Stack>
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="City"
                    value={vCard.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="State/Province"
                    value={vCard.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="ZIP/Postal Code"
                    value={vCard.zip}
                    onChange={(e) => handleInputChange("zip", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="Country"
                    value={vCard.country}
                    onChange={(e) => handleInputChange("country", e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Divider />
              <Typography variant="subtitle1" fontWeight="bold">
                Other
              </Typography>

              <TextField
                size="small"
                label="Website"
                value={vCard.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Website 2"
                value={vCard.website2}
                onChange={(e) => handleInputChange("website2", e.target.value)}
                fullWidth
              />

              {/* <TextField
                size="small"
                label="Notes"
                value={vCard.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                fullWidth
                multiline
                rows={3}
              /> */}
            </Stack>
          </Paper>
        </Grid>

        {/* QR Code Section */}
        <Grid item xs={12} md={6}>
          <Paper>
            <Typography variant="h6" gutterBottom>
              QR Code Preview
            </Typography>

            <Stack spacing={1}>
              <Typography variant="subtitle2">Select size</Typography>
              <Stack direction="row" spacing={2}>
                {sizeOptions.map(([sizeOption, label]) => (
                  <Button
                    key={label}
                    onClick={() => setSize(sizeOption)}
                    variant="contained"
                    color={sizeOption === size ? "secondary" : "primary"}
                    size="small"
                  >
                    {label}
                  </Button>
                ))}
                <TextField
                  size="small"
                  label="Size (pixels)"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value) || 256)}
                  type="number"
                  inputProps={{ min: 256, max: 1000, step: 50 }}
                  sx={{ width: "100px" }}
                />
              </Stack>
              {(size < sizeOptions[0][0] || size < vCardString.length) && (
                <Alert severity="warning">
                  The size may be too small for the QR code to be readable. 
                  Check readibility on the QR code preview using an appropriate device.
                </Alert>
              )}

              <Typography variant="subtitle2">Select color</Typography>
              <Stack direction="row" spacing={2}>
                {colorOptions.map((option, index) => (
                  <Button
                    key={index}
                    onClick={() => setColor(option)}
                    variant="contained"
                    color={option.name === color.name ? "secondary" : "primary"}
                  >
                    {option.name}
                  </Button>
                ))}
              </Stack>

              {error && <Alert severity="error">{error}</Alert>}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <QRCode
                  id="vcard-qr-code"
                  value={vCardString}
                  ref={qrCodeRef}
                  size={size}
                  logoImage={false}
                  logoWidth={size / 4}
                  qrStyle="square"
                  fgColor={color.fgcolor}
                  bgColor={color.bgcolor}
                  ecLevel="L"
                />
              </Box>

              <Button
                startIcon={<Download />}
                onClick={downloadQRCode}
                variant="contained"
                fullWidth
                disabled={(!vCard.firstName && !vCard.lastName) || error}
              >
                Download vCard
              </Button>
              {/* <pre>
                {vCardString}
              </pre> */}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
